import { createContext, useContext, useEffect, useState, type ReactNode } from "react";
import { API_BASE } from "@/config/api";
import { buildWhatsappLink } from "@/lib/whatsapp";
import { normalizeMediaUrls } from "@/lib/mediaUrl";
import type { BootstrapData } from "@/types/content";

interface SiteDataValue {
  data: BootstrapData;
  whatsappLink: (message?: string) => string;
}

const SiteDataContext = createContext<SiteDataValue | null>(null);

export function useSiteData(): SiteDataValue {
  const ctx = useContext(SiteDataContext);
  if (!ctx) throw new Error("useSiteData debe usarse dentro de <SiteDataProvider>");
  return ctx;
}

export function SiteDataProvider({ children }: { children: ReactNode }) {
  const [data, setData] = useState<BootstrapData | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    fetch(`${API_BASE}/bootstrap`)
      .then((res) => {
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        return res.json();
      })
      .then((json: BootstrapData) => {
        if (!cancelled) setData(normalizeMediaUrls(json));
      })
      .catch((err) => {
        if (!cancelled) setError(err instanceof Error ? err.message : "Error desconocido");
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!data) return;
    document.title = data.siteSettings.seo_title || data.siteSettings.brand;
    const setMeta = (name: string, content: string) => {
      if (!content) return;
      let tag = document.querySelector(`meta[name="${name}"]`);
      if (!tag) {
        tag = document.createElement("meta");
        tag.setAttribute("name", name);
        document.head.appendChild(tag);
      }
      tag.setAttribute("content", content);
    };
    setMeta("description", data.siteSettings.seo_description);
    setMeta("keywords", data.siteSettings.seo_keywords);
  }, [data]);

  if (error) {
    return (
      <div className="flex min-h-screen items-center justify-center px-4 text-center">
        <div>
          <p className="text-lg font-semibold text-foreground-900">No pudimos cargar el sitio</p>
          <p className="mt-2 text-sm text-foreground-500">Intenta recargar la página en unos segundos.</p>
        </div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <div className="w-10 h-10 rounded-full border-2 border-primary-200 border-t-primary-500 animate-spin" />
      </div>
    );
  }

  const value: SiteDataValue = {
    data,
    whatsappLink: (message?: string) =>
      buildWhatsappLink(data.siteSettings.phone_raw, message || data.siteSettings.whatsapp_default_message),
  };

  return <SiteDataContext.Provider value={value}>{children}</SiteDataContext.Provider>;
}
